import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { LiveProvider } from '../api/live'
import { createQueryClient } from '../api/queryClient'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { reportCrash } from '../lib/reporter'
import { cssVariablesResolver, theme } from '../styles/theme'
import { createAppRouter } from './router'

export interface ProvidersProps {
  /** Injectable so tests get an isolated cache per case. */
  readonly queryClient?: QueryClient
  readonly children?: ReactNode
}

/**
 * The provider stack — docs/spec/01 §2.
 *
 * Order matters: the ErrorBoundary is outermost so a crash inside any provider
 * still renders the recoverable fallback rather than a white screen (01 §7).
 */
export function AppProviders({ queryClient, children }: ProvidersProps) {
  const client = queryClient ?? createQueryClient()

  return (
    // A render crash IS unexpected, so it is reported (06 §6) — with the
    // component stack redacted, since a stack can carry rendered PII.
    <ErrorBoundary onError={(error, info) => reportCrash(error, { componentStack: info })}>
      <MantineProvider
        theme={theme}
        defaultColorScheme="auto"
        cssVariablesResolver={cssVariablesResolver}
        // jsdom implements no layout, so Floating UI's `hide()` middleware
        // decides every popover trigger is clipped and Mantine stamps
        // `display: none` on the dropdown a few frames after it opens — pulling
        // it out of the accessibility tree mid-test. `env="test"` is Mantine's
        // own switch for exactly this (08 §5).
        //
        // Spread rather than `env={cond ? 'test' : undefined}`: under
        // `exactOptionalPropertyTypes` an optional prop may be omitted but not
        // explicitly passed as `undefined`. Vite statically replaces
        // `import.meta.env.MODE`, so a production build drops the prop entirely.
        {...(import.meta.env.MODE === 'test' ? { env: 'test' as const } : {})}
      >
        <QueryClientProvider client={client}>
          {/*
            Inside the QueryClientProvider because the live socket's whole job is
            patching that cache (03 §3). With no VITE_WS_URL it opens nothing, so
            the app runs on HTTP + polling exactly as before.
          */}
          <LiveProvider>
            <Notifications position="bottom-right" />
            {children}
          </LiveProvider>
        </QueryClientProvider>
      </MantineProvider>
    </ErrorBoundary>
  )
}

const router = createAppRouter()

/** The whole app: providers wrapped around the router. */
export function App() {
  return (
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  )
}
